// AI Service - Muse AI prompt management
import {
    collection,
    doc,
    getDocs,
    addDoc,
    query,
    where,
    orderBy,
    limit,
    serverTimestamp,
    increment,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';
import { db } from './firebase.js';
import { updateUserProfile } from './firestore.js';
import { generateMusePromptAI } from './gemini.js';

// Default Muse prompts to seed the database
const DEFAULT_PROMPTS = [
    { text: "Why not try painting with coffee?", category: "painting", author: "Muse", uses: 0, status: 'approved' },
    { text: "Write a story that starts with 'The clock struck 13'.", category: "writing", author: "Muse", uses: 0, status: 'approved' },
    { text: "Go for a walk and take photos of only red things.", category: "photography", author: "Muse", uses: 0, status: 'approved' },
    { text: "Design a flag for a country that doesn't exist.", category: "design", author: "Muse", uses: 0, status: 'approved' },
    { text: "Compose a haiku about your breakfast.", category: "writing", author: "Muse", uses: 0, status: 'approved' },
    { text: "Sketch the view from your window using only straight lines.", category: "drawing", author: "Muse", uses: 0, status: 'approved' },
    { text: "Use a palette knife to create a texture based on tree bark.", category: "painting", author: "Muse", uses: 0, status: 'approved' },
    { text: "Model a low-poly vehicle that runs on wind power.", category: "3d", author: "Muse", uses: 0, status: 'approved' },
    { text: "Sketch your non-dominant hand holding an object.", category: "drawing", author: "Muse", uses: 0, status: 'approved' },
    { text: "Sculpt a small creature using only recycled materials.", category: "sculpting", author: "Muse", uses: 0, status: 'approved' },
    { text: "Create a 3D scene with dramatic lighting.", category: "3d", author: "Muse", uses: 0, status: 'approved' },
    { text: "Paint a landscape using only 3 colors.", category: "painting", author: "Muse", uses: 0, status: 'approved' }
];

// Seed default prompts (call this once during setup)
export const seedDefaultPrompts = async () => {
    try {
        const promptsRef = collection(db, 'musePrompts');
        const querySnapshot = await getDocs(promptsRef);

        // Only seed if no prompts exist
        if (querySnapshot.empty) {
            console.log('Seeding default Muse prompts...');
            for (const prompt of DEFAULT_PROMPTS) {
                await addDoc(promptsRef, {
                    ...prompt,
                    createdAt: serverTimestamp()
                });
            }
            console.log('Default prompts seeded successfully!');
        }
    } catch (error) {
        console.error('Error seeding prompts:', error);
        throw error;
    }
};

// Moderate content (basic client-side check)
const moderateContent = (text) => {
    const bannedWords = ['hate', 'violence', 'bad', 'kill', 'stupid'];
    const lowerText = text.toLowerCase();
    return !bannedWords.some(word => lowerText.includes(word));
};

// Get random approved prompt, optionally filtered by category
// Get random approved prompt, optionally filtered by category
export const askMuse = async (userId, category = 'all', tier = 'global', isSuperAdmin = false, isFirstDailyUse = false) => {
    try {
        // HYBRID STRATEGY: 
        // Global Tier = Database Only (Free, 0 Tokens) -> EXCEPT first use of the day (One Daily Gift)
        // Paid/Admin = AI First (Tokens, contributes to DB)

        const isPaidTier = tier?.includes('subscriber') || tier?.includes('admin');
        const shouldUseAI = isSuperAdmin || isPaidTier || (tier === 'global' && isFirstDailyUse);

        // 1. Try AI Generation (ONLY for Paid/Admin)
        if (shouldUseAI) {
            const aiPrompt = await generateMusePromptAI(category);

            if (aiPrompt) {
                // Success! 
                // A. Add to user history
                const historyItem = await addToMuseHistory(userId, aiPrompt, category);

                // B. Save this new spark to the shared DB for free users to enjoy later!
                // This builds the library automatically.
                const promptsRef = collection(db, 'musePrompts');
                await addDoc(promptsRef, {
                    text: aiPrompt,
                    category,
                    author: 'AI', // Marked as AI generated
                    authorName: 'Muse AI',
                    uses: 0,
                    status: 'approved', // Auto-approve AI content
                    createdAt: serverTimestamp()
                });

                // Update user stats
                await updateUserProfile(userId, {
                    'stats.museUsage': increment(1)
                });

                return {
                    text: aiPrompt,
                    category: category,
                    historyItem
                };
            }
        }

        console.log("Using Database Archive (Free Tier or AI Fallback).");

        const promptsRef = collection(db, 'musePrompts');

        // Build query for approved prompts
        let q;
        if (category === 'all') {
            q = query(promptsRef, where('status', '==', 'approved'));
        } else {
            q = query(
                promptsRef,
                where('status', '==', 'approved'),
                where('category', '==', category)
            );
        }

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            // Fallback: get any approved prompt if category has none
            const fallbackQuery = query(promptsRef, where('status', '==', 'approved'));
            const fallbackSnapshot = await getDocs(fallbackQuery);

            if (fallbackSnapshot.empty) {
                throw new Error('No prompts available');
            }

            // Select random prompt
            const docs = fallbackSnapshot.docs;
            const randomDoc = docs[Math.floor(Math.random() * docs.length)];
            const promptData = randomDoc.data();

            // Increment use count
            await updateDoc(randomDoc.ref, { uses: increment(1) });

            // Add to user's history
            const historyItem = await addToMuseHistory(userId, promptData.text, promptData.category);

            // Update user stats
            await updateUserProfile(userId, {
                'stats.museUsage': increment(1)
            });

            return {
                text: promptData.text,
                category: promptData.category,
                historyItem
            };
        }

        // Select random prompt from filtered results
        const docs = querySnapshot.docs;
        const randomDoc = docs[Math.floor(Math.random() * docs.length)];
        const promptData = randomDoc.data();

        // Increment use count
        await updateDoc(randomDoc.ref, { uses: increment(1) });

        // Add to user's history
        const historyItem = await addToMuseHistory(userId, promptData.text, category);

        // Update user stats
        await updateUserProfile(userId, {
            'stats.museUsage': increment(1)
        });

        return {
            text: promptData.text,
            category: category,
            historyItem
        };
    } catch (error) {
        console.error('Error asking Muse:', error);
        throw error;
    }
};

// Submit a user-generated prompt
export const submitPrompt = async (userId, userName, text, category) => {
    try {
        const isSafe = moderateContent(text);

        const promptsRef = collection(db, 'musePrompts');
        await addDoc(promptsRef, {
            text,
            category,
            author: userId,
            authorName: userName,
            uses: 0,
            status: isSafe ? 'approved' : 'rejected',
            createdAt: serverTimestamp()
        });

        return { status: isSafe ? 'approved' : 'rejected' };
    } catch (error) {
        console.error('Error submitting prompt:', error);
        throw error;
    }
};

// Get user's Muse history
export const getMuseHistory = async (userId, limitCount = 50) => {
    try {
        const historyRef = collection(db, 'museHistory');
        const q = query(
            historyRef,
            where('userId', '==', userId),
            orderBy('date', 'desc'),
            limit(limitCount)
        );

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore timestamp to readable date
            date: doc.data().date?.toDate().toLocaleDateString() || new Date().toLocaleDateString()
        }));
    } catch (error) {
        console.error('Error getting Muse history:', error);
        throw error;
    }
};

// Add to user's Muse history
export const addToMuseHistory = async (userId, prompt, category) => {
    try {
        const historyRef = collection(db, 'museHistory');
        const docRef = await addDoc(historyRef, {
            userId,
            prompt,
            category,
            date: serverTimestamp(),
            favorite: false // Default to not favorite
        });

        return {
            id: docRef.id,
            prompt,
            category,
            favorite: false,
            date: new Date().toLocaleDateString()
        };
    } catch (error) {
        console.error('Error adding to Muse history:', error);
        throw error;
    }
};

// Delete a history item
export const deleteMuseHistory = async (historyId) => {
    try {
        await deleteDoc(doc(db, 'museHistory', historyId));
        return true;
    } catch (error) {
        console.error('Error deleting history:', error);
        throw error;
    }
};

// Toggle favorite status
export const toggleMuseFavorite = async (historyId, isFavorite) => {
    try {
        const historyRef = doc(db, 'museHistory', historyId);
        await updateDoc(historyRef, {
            favorite: !isFavorite
        });
        return !isFavorite;
    } catch (error) {
        console.error('Error toggling favorite:', error);
        throw error;
    }
};

// Cleanup old history items based on retention limit
// Keeps 'limit' * 2 items (e.g., 5 visible + 5 buffer = 10 total)
// Favorites are ALWAYS kept and do not count towards the deletion limit logic here (or can be skipped)
export const cleanupOldMuseHistory = async (userId, retentionLimit) => {
    try {
        const historyRef = collection(db, 'museHistory');

        // Query ALL items for this user, ordered by date
        const q = query(
            historyRef,
            where('userId', '==', userId),
            orderBy('date', 'desc')
        );
        const snapshot = await getDocs(q);

        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filter out favorites (they are safe)
        const nonFavorites = items.filter(item => !item.favorite);

        // If non-favorites exceed retention limit, delete the oldest ones
        if (nonFavorites.length > retentionLimit) {
            const itemsToDelete = nonFavorites.slice(retentionLimit); // Get everything after the limit

            console.log(`Cleaning up ${itemsToDelete.length} old Muse items...`);

            const deletePromises = itemsToDelete.map(item =>
                deleteDoc(doc(db, 'museHistory', item.id))
            );

            await Promise.all(deletePromises);
            return itemsToDelete.length;
        }
        return 0;
    } catch (error) {
        console.error("Error cleaning up history:", error);
        return 0; // Fail silently to user
    }
};
